import { Observable, catchError, forkJoin, map, of, switchMap, throwError } from 'rxjs';
import type { GridOrchestrationDataAccess } from './grid-orchestration-data-access';
import { take } from 'rxjs/operators';
import { GridRow, GridRowsPage, type GridSortField } from '../contracts/public-api';
import { asGridSourceRow } from './grid-source-row.interface';
import type { GridBackendAttributeFilter, ColumnFilterMap } from '../d-data-grid/column-filter.types';
import type { DataGridColumn } from '../d-data-grid/data-grid.types';
import type { GridColumnRemapping } from '../contracts/data-grid-config.interface';
import {
  buildCurrentAttributeFilters,
  buildNullOperatorEntry,
  hasCanonicalNullAliasFilter,
  splitWhereByCanonicalAlias
} from './grid-orchestration-attribute-filter';
import { isNullBucketPath as pathIsNullBucket } from './grid-orchestration-bucket-path';
import {
  GROUP_PATH_DELIMITER,
  NULL_BUCKET_MAX_SCAN_PAGES,
  NULL_DISPLAY_KEY
} from './grid-orchestration.constants';
import { GridOrchestrationLogLevel, logGridOrchestration } from './grid-orchestration.dev-log';
import { bucketKeyToFilterValue } from './grid-orchestration.utils';

/** Frozen empty attrs object used to avoid per-row `{}` allocations. */
const EMPTY_ATTRS: Readonly<Record<string, unknown>> = Object.freeze({});

/**
 * State accessors needed by null-bucket resolution functions.
 * Implemented by {@link GridOrchestrationService} via {@link buildNullBucketHost}.
 */
export interface NullBucketHost {
  readonly elementsProvider: GridOrchestrationDataAccess;
  getRemappedFields: () => ReadonlySet<string>;
  getSharedColumns: () => Array<DataGridColumn>;
  getTypeSpecificColumns: () => Record<string, Array<DataGridColumn>>;
  getColumnFilters: () => ColumnFilterMap;
  getRemappingLookup: () => ReadonlyMap<string, string>;
  getGridGroupByFields: () => Array<string>;
  getGridSort: () => Array<GridSortField> | null;
  getColumnRemappings: () => ReadonlyArray<GridColumnRemapping>;
  getElementTypeDisplayNames: () => Record<string, string>;
  getDiscriminatorField: () => string;
}

// ── Pure helpers ────────────────────────────────────────────────────────────

/** True when the response contains no rows and reports zero matches. */
export function isEmptyElementsResponse(response: GridRowsPage): boolean {
  return (response.totalCount ?? 0) === 0 && response.nodes.length === 0;
}

/** Reads discriminator-field `in` from a HotChocolate-style where clause. */
export function extractDiscriminatorTypesFromWhere(
  where: Record<string, unknown>,
  discriminatorField: string
): Array<string> {
  if (discriminatorField.length === 0) {
    return new Array<string>();
  }
  const clause: unknown = where[discriminatorField];
  if (typeof clause === 'object' && clause !== null && 'in' in clause) {
    const types: unknown = (clause as { in: unknown }).in;
    if (Array.isArray(types)) {
      return types.filter((type: unknown): type is string => typeof type === 'string');
    }
  }
  return new Array<string>();
}

/** True when a field value represents a null/blank bucket (matches summary `(blank)`). */
function isNullFieldValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/** Case-insensitive label match tolerant of singular/plural display names. */
function elementTypeLabelsMatch(left: string, right: string): boolean {
  const normalizeLabel: (label: string) => string = (label: string): string =>
    label.trim().toLowerCase().replace(/s$/, '');
  const normalizedLeft: string = normalizeLabel(left);
  const normalizedRight: string = normalizeLabel(right);
  return (
    normalizedLeft === normalizedRight ||
    normalizedLeft.startsWith(normalizedRight) ||
    normalizedRight.startsWith(normalizedLeft)
  );
}

/**
 * Whether a remapping `elementType` entry applies to the scoped bucket slug.
 * Summary-tree paths use tile-layer ids; remappings may use schema entity names.
 */
export function remappingElementTypeMatchesScopedType(
  remapElementType: string,
  scopedType: string,
  discriminatorLabels: Record<string, string>
): boolean {
  if (remapElementType.toLowerCase() === scopedType.toLowerCase()) {
    return true;
  }
  const remapDisplay: string | undefined = discriminatorLabels[remapElementType];
  const scopedDisplay: string | undefined = discriminatorLabels[scopedType];
  if (remapDisplay !== undefined && scopedDisplay !== undefined) {
    return elementTypeLabelsMatch(remapDisplay, scopedDisplay);
  }
  if (remapDisplay !== undefined && elementTypeLabelsMatch(remapDisplay, scopedType)) {
    return true;
  }
  if (scopedDisplay !== undefined && elementTypeLabelsMatch(scopedDisplay, remapElementType)) {
    return true;
  }
  return false;
}

/** Resolves the entity-specific JSONB field for a canonical alias and element type. */
export function resolveEntityAttributeField(
  canonicalField: string,
  elementType: string,
  columnRemappings: ReadonlyArray<GridColumnRemapping>,
  discriminatorLabels: Record<string, string>
): string | undefined {
  const remap: GridColumnRemapping | undefined = columnRemappings.find(
    (r: GridColumnRemapping): boolean => r.canonicalField === canonicalField
  );
  if (remap === undefined) {
    return undefined;
  }
  const match: { discriminatorValue: string; field: string } | undefined = remap.fields.find(
    (f: { discriminatorValue: string; field: string }): boolean =>
      remappingElementTypeMatchesScopedType(f.discriminatorValue, elementType, discriminatorLabels)
  );
  return match?.field;
}

/** Resolves a grouped field value from an element node (top-level or remapped JSONB). */
export function resolveNodeFieldValue(
  node: GridRow,
  field: string,
  remappingLookup: ReadonlyMap<string, string>,
  discriminatorField: string
): unknown {
  const source = asGridSourceRow(node);
  if (field === discriminatorField) {
    return source[discriminatorField];
  }
  if (field in node) {
    const topLevel: unknown = Reflect.get(node, field);
    if (topLevel !== undefined) {
      return topLevel;
    }
  }
  const attrs: Readonly<Record<string, unknown>> = source.attributes ?? EMPTY_ATTRS;
  if (attrs[field] !== undefined) {
    return attrs[field];
  }
  for (const attrKey in attrs) {
    if (remappingLookup.get(attrKey) === field) {
      return attrs[attrKey];
    }
  }
  return undefined;
}

/** Whether an element belongs in the bucket identified by `path`. */
export function nodeMatchesBucketPath(
  path: string,
  node: GridRow,
  gridGroupByFields: Array<string>,
  remappingLookup: ReadonlyMap<string, string>,
  discriminatorLabels: Record<string, string>,
  discriminatorField: string
): boolean {
  const segments: Array<string> = path.split(GROUP_PATH_DELIMITER);
  for (let i: number = 0; i < gridGroupByFields.length && i < segments.length; i++) {
    const field: string = gridGroupByFields[i];
    const segment: string = segments[i];
    const expected: string | null = bucketKeyToFilterValue(segment, NULL_DISPLAY_KEY);
    const value: unknown = resolveNodeFieldValue(node, field, remappingLookup, discriminatorField);
    if (expected === null) {
      if (!isNullFieldValue(value)) {
        return false;
      }
      continue;
    }
    if (field === discriminatorField) {
      if (!remappingElementTypeMatchesScopedType(String(value), segment, discriminatorLabels)) {
        return false;
      }
      continue;
    }
    if (String(value) !== expected) {
      return false;
    }
  }
  return true;
}

/**
 * Whether a bucket path includes a null/Unknown segment for any grouped field.
 */
export function isNullBucketPath(path: string, gridGroupByFields: Array<string>): boolean {
  return pathIsNullBucket(path, gridGroupByFields);
}

// ── Query helpers ────────────────────────────────────────────────────────────

/** Variables sent with a single `elements` grid page request. */
interface ElementsGridQueryVariables {
  /** Page size. */
  readonly first: number;
  /** Filter payload, with canonical aliases already lifted out. */
  readonly where: Record<string, unknown>;
  /** HotChocolate cursor, when continuing a connection. */
  readonly after?: string;
  /** Active sort, when any. */
  readonly sort?: Array<GridSortField>;
  /** Canonical-alias and column filters. */
  readonly attributeFilter?: Array<GridBackendAttributeFilter>;
}

/** One entry of a GraphQL `errors[]` array. */
interface GraphQLErrorDetail {
  /** Server-supplied message. */
  readonly message: string;
  /** Response path the error was raised for. */
  readonly path?: ReadonlyArray<string | number>;
  /** Server metadata, typically carrying `code`. */
  readonly extensions?: Record<string, unknown>;
}

/** Narrows an error to an Apollo error carrying a GraphQL `errors` array. */
function hasGraphQLErrorDetails(
  error: unknown
): error is Error & { readonly errors: ReadonlyArray<GraphQLErrorDetail> } {
  return error instanceof Error && Array.isArray((error as { errors?: unknown }).errors);
}

/**
 * Logs the operation, variables and `errors[]` for a failed `elements` page.
 *
 * The server answers a rejected page with an opaque `Unhandled exception`, which
 * is unactionable without the request that produced it.
 *
 * @param error - The error thrown by the data access layer.
 * @param variables - The exact variables sent with the query.
 */
function logElementsGridFailure(error: unknown, variables: ElementsGridQueryVariables): void {
  const details: Array<GraphQLErrorDetail> = hasGraphQLErrorDetails(error) ? [...error.errors] : [];
  logGridOrchestration(
    GridOrchestrationLogLevel.Error,
    '[GridDiag] ElementsForGrid failed.',
    {
      operationName: 'ElementsForGrid',
      variables,
      errors: details.map((detail: GraphQLErrorDetail): Record<string, unknown> => ({
        message: detail.message,
        path: detail.path ?? null,
        code: detail.extensions?.['code'] ?? null,
        extensions: detail.extensions ?? null
      }))
    },
    error
  );
}

/**
 * Executes a single `elements` grid query with the given filter payload.
 */
export function queryElementsGrid(
  host: NullBucketHost,
  where: Record<string, unknown>,
  attributeFilter: Array<GridBackendAttributeFilter> | undefined,
  after?: string
): Observable<GridRowsPage> {
  const PAGE_SIZE: number = 100;
  const sort: Array<GridSortField> | null = host.getGridSort();
  const variables: ElementsGridQueryVariables = {
    first: PAGE_SIZE,
    where,
    ...(after !== undefined && { after }),
    ...(sort !== null && sort.length > 0 && { sort }),
    ...(attributeFilter !== undefined && { attributeFilter })
  };

  return host.elementsProvider.getElementsForGrid(variables).pipe(
    take(1),
    catchError((err: Error): Observable<never> => {
      logElementsGridFailure(err, variables);
      return throwError((): Error => err);
    })
  );
}

// ── Null-alias resolution ────────────────────────────────────────────────────

/**
 * For single-type buckets, map canonical-alias null checks to the entity JSONB key
 * from column remappings (e.g. `yearBuilt` → `P_YEAR_CONSTRUCTED` for pavements).
 */
export function resolveNullAliasAttributeFilters(
  where: Record<string, unknown>,
  extras: ReadonlyArray<GridBackendAttributeFilter>,
  remappedFields: ReadonlySet<string>,
  columnRemappings: ReadonlyArray<GridColumnRemapping>,
  discriminatorLabels: Record<string, string>,
  discriminatorField: string
): Array<GridBackendAttributeFilter> {
  const scopedTypes: Array<string> = extractDiscriminatorTypesFromWhere(where, discriminatorField);
  if (scopedTypes.length !== 1) {
    return [...extras];
  }
  const scopedType: string = scopedTypes[0];
  const resolved: Array<GridBackendAttributeFilter> = new Array<GridBackendAttributeFilter>();

  for (const entry of extras) {
    if (entry.operator !== 'IS_NULL' && entry.operator !== 'IS_NOT_NULL') {
      resolved.push(entry);
      continue;
    }
    if (!remappedFields.has(entry.name)) {
      resolved.push(buildNullOperatorEntry(entry.name, entry.operator));
      continue;
    }
    const entityField: string | undefined = resolveEntityAttributeField(
      entry.name,
      scopedType,
      columnRemappings,
      discriminatorLabels
    );
    const name: string = entityField ?? entry.name;
    resolved.push(buildNullOperatorEntry(name, entry.operator));
  }
  return resolved;
}

/**
 * Builds entity-specific IS_NULL filters for every remapped field entry matching
 * the scoped element types (used as a retry when canonical null filters return empty).
 */
export function buildEntitySpecificNullFilters(
  where: Record<string, unknown>,
  extras: ReadonlyArray<GridBackendAttributeFilter>,
  remappedFields: ReadonlySet<string>,
  columnRemappings: ReadonlyArray<GridColumnRemapping>,
  discriminatorLabels: Record<string, string>,
  discriminatorField: string
): Array<GridBackendAttributeFilter> {
  const scopedTypes: Array<string> = extractDiscriminatorTypesFromWhere(where, discriminatorField);
  const result: Array<GridBackendAttributeFilter> = new Array<GridBackendAttributeFilter>();

  for (const entry of extras) {
    if (entry.operator !== 'IS_NULL' && entry.operator !== 'IS_NOT_NULL') {
      continue;
    }
    if (!remappedFields.has(entry.name)) {
      continue;
    }
    const remap: GridColumnRemapping | undefined = columnRemappings.find(
      (r: GridColumnRemapping): boolean => r.canonicalField === entry.name
    );
    if (remap === undefined) {
      continue;
    }
    for (const fieldEntry of remap.fields) {
      if (
        scopedTypes.length > 0 &&
        !scopedTypes.some((type: string): boolean =>
          remappingElementTypeMatchesScopedType(fieldEntry.discriminatorValue, type, discriminatorLabels)
        )
      ) {
        continue;
      }
      result.push(buildNullOperatorEntry(fieldEntry.field, entry.operator));
    }
  }
  return result;
}

// ── Grid page fetching ───────────────────────────────────────────────────────

/**
 * Strips null-alias `attributeFilter` constraints from a bucket `where` clause so
 * rows can be scanned and matched client-side when `IS_NULL` fails on the backend.
 */
function buildUnfilteredWhereForNullBucket(
  host: NullBucketHost,
  bucketWhere: Record<string, unknown>
): Record<string, unknown> {
  const split: {
    where: Record<string, unknown>;
    extraAttributeFilter: Array<GridBackendAttributeFilter>;
    unsupportedAliases: Array<string>;
  } = splitWhereByCanonicalAlias(
    bucketWhere,
    host.getRemappedFields(),
    host.getSharedColumns(),
    host.getTypeSpecificColumns()
  );
  return split.where;
}

/**
 * Retries a null-alias bucket by querying each scoped element type separately.
 * Avoids AND-ing incompatible entity JSONB null checks across types.
 */
function fetchNullAliasBucketPerType(
  host: NullBucketHost,
  where: Record<string, unknown>,
  extras: ReadonlyArray<GridBackendAttributeFilter>,
  columnAttributeFilter: ReadonlyArray<GridBackendAttributeFilter>,
  after?: string
): Observable<GridRowsPage> {
  const discriminatorField: string = host.getDiscriminatorField();
  const scopedTypes: Array<string> = extractDiscriminatorTypesFromWhere(where, discriminatorField);
  if (scopedTypes.length === 0) {
    return of({ nodes: [], totalCount: 0 });
  }
  const remappedFields: ReadonlySet<string> = host.getRemappedFields();
  const columnRemappings: ReadonlyArray<GridColumnRemapping> = host.getColumnRemappings();
  const discriminatorLabels: Record<string, string> = host.getElementTypeDisplayNames();

  const queries: Array<Observable<GridRowsPage>> = scopedTypes.map(
    (type: string): Observable<GridRowsPage> => {
      const typeWhere: Record<string, unknown> = { ...where };
      if (discriminatorField.length > 0) {
        typeWhere[discriminatorField] = { in: [type] };
      }
      const typeExtras: Array<GridBackendAttributeFilter> = resolveNullAliasAttributeFilters(
        typeWhere,
        extras,
        remappedFields,
        columnRemappings,
        discriminatorLabels,
        discriminatorField
      );
      const mergedFilter: Array<GridBackendAttributeFilter> =
        typeExtras.length === 0 ? [...columnAttributeFilter] : [...columnAttributeFilter, ...typeExtras];
      const attributeFilter: Array<GridBackendAttributeFilter> | undefined =
        mergedFilter.length > 0 ? mergedFilter : undefined;
      return queryElementsGrid(host, typeWhere, attributeFilter, after);
    }
  );
  return forkJoin(queries).pipe(
    map((responses: Array<GridRowsPage>): GridRowsPage => {
      const allNodes: Array<GridRow> = responses.flatMap((response: GridRowsPage): Array<GridRow> => [
        ...response.nodes
      ]);
      const totalCount: number = responses.reduce(
        (sum: number, response: GridRowsPage): number => sum + (response.totalCount ?? response.nodes.length),
        0
      );
      const lastResponse: GridRowsPage = responses[responses.length - 1];
      return {
        nodes: allNodes,
        totalCount,
        pageInfo: lastResponse.pageInfo
      };
    })
  );
}

/**
 * Recursively scans unfiltered pages until enough null-bucket matches are collected.
 */
function scanUnfilteredPagesForNullBucket(
  host: NullBucketHost,
  path: string,
  where: Record<string, unknown>,
  attributeFilter: Array<GridBackendAttributeFilter> | undefined,
  scanAfter: string | undefined,
  targetNewRows: number,
  expectedTotal: number,
  alreadyLoaded: number,
  pagesScanned: number,
  newMatches: Array<GridRow>
): Observable<GridRowsPage> {
  if (targetNewRows === 0) {
    return of({
      nodes: [],
      totalCount: expectedTotal,
      pageInfo: { endCursor: scanAfter ?? null, hasNextPage: false }
    });
  }
  if (pagesScanned >= NULL_BUCKET_MAX_SCAN_PAGES) {
    const hasMore: boolean = alreadyLoaded + newMatches.length < expectedTotal;
    return of({
      nodes: newMatches,
      totalCount: expectedTotal,
      pageInfo: { endCursor: scanAfter ?? null, hasNextPage: hasMore }
    });
  }

  const gridGroupByFields: Array<string> = host.getGridGroupByFields();
  const remappingLookup: ReadonlyMap<string, string> = host.getRemappingLookup();
  const discriminatorLabels: Record<string, string> = host.getElementTypeDisplayNames();
  const discriminatorField: string = host.getDiscriminatorField();

  return queryElementsGrid(host, where, attributeFilter, scanAfter).pipe(
    switchMap((response: GridRowsPage): Observable<GridRowsPage> => {
      for (const node of response.nodes) {
        if (newMatches.length >= targetNewRows) {
          break;
        }
        if (
          nodeMatchesBucketPath(
            path,
            node,
            gridGroupByFields,
            remappingLookup,
            discriminatorLabels,
            discriminatorField
          )
        ) {
          newMatches.push(node);
        }
      }
      const scanCursor: string | null = response.pageInfo?.endCursor ?? null;
      const scanHasNext: boolean = response.pageInfo?.hasNextPage ?? false;
      const bucketHasMore: boolean = alreadyLoaded + newMatches.length < expectedTotal;

      if (newMatches.length >= targetNewRows || !scanHasNext) {
        return of({
          nodes: newMatches,
          totalCount: expectedTotal,
          pageInfo: {
            endCursor: scanCursor,
            hasNextPage: bucketHasMore && scanHasNext
          }
        });
      }

      return scanUnfilteredPagesForNullBucket(
        host,
        path,
        where,
        attributeFilter,
        scanCursor ?? undefined,
        targetNewRows,
        expectedTotal,
        alreadyLoaded,
        pagesScanned + 1,
        newMatches
      );
    })
  );
}

// ── Public entry points ──────────────────────────────────────────────────────

/**
 * Fetches a single page of elements for the grid using the grid-optimised query.
 * Handles canonical alias stripping and null-bucket fallback logic.
 */
export function fetchGridPage(
  host: NullBucketHost,
  logWarning: (message: string) => void,
  where: Record<string, unknown>,
  after?: string
): Observable<GridRowsPage> {
  const remappedFields: ReadonlySet<string> = host.getRemappedFields();
  const sharedColumns: Array<DataGridColumn> = host.getSharedColumns();
  const discriminatorColumns: Record<string, Array<DataGridColumn>> = host.getTypeSpecificColumns();
  const columnFilters: ColumnFilterMap = host.getColumnFilters();
  const columnRemappings: ReadonlyArray<GridColumnRemapping> = host.getColumnRemappings();
  const discriminatorLabels: Record<string, string> = host.getElementTypeDisplayNames();
  const discriminatorField: string = host.getDiscriminatorField();

  const split: {
    where: Record<string, unknown>;
    extraAttributeFilter: Array<GridBackendAttributeFilter>;
    unsupportedAliases: Array<string>;
  } = splitWhereByCanonicalAlias(where, remappedFields, sharedColumns, discriminatorColumns);

  if (split.unsupportedAliases.length > 0) {
    logWarning(
      `fetchGridPage: dropping unsupported canonical-alias 'where' clause(s) for ` +
        `[${split.unsupportedAliases.join(', ')}]. The resulting query may include rows you did not intend.`
    );
  }

  const columnAttributeFilter: Array<GridBackendAttributeFilter> = buildCurrentAttributeFilters(
    columnFilters,
    sharedColumns,
    discriminatorColumns,
    remappedFields
  );
  const allAttributeFilter: Array<GridBackendAttributeFilter> =
    split.extraAttributeFilter.length === 0
      ? columnAttributeFilter
      : [
          ...columnAttributeFilter,
          ...resolveNullAliasAttributeFilters(
            split.where,
            split.extraAttributeFilter,
            remappedFields,
            columnRemappings,
            discriminatorLabels,
            discriminatorField
          )
        ];
  const attributeFilter: Array<GridBackendAttributeFilter> | undefined =
    allAttributeFilter.length > 0 ? allAttributeFilter : undefined;

  return queryElementsGrid(host, split.where, attributeFilter, after).pipe(
    switchMap((response: GridRowsPage): Observable<GridRowsPage> => {
      if (
        after !== undefined ||
        !isEmptyElementsResponse(response) ||
        !hasCanonicalNullAliasFilter(split.extraAttributeFilter, remappedFields)
      ) {
        return of(response);
      }
      const scopedTypes: Array<string> = extractDiscriminatorTypesFromWhere(split.where, discriminatorField);
      if (scopedTypes.length > 1) {
        return fetchNullAliasBucketPerType(
          host,
          split.where,
          split.extraAttributeFilter,
          columnAttributeFilter,
          after
        );
      }
      const entityNullFilters: Array<GridBackendAttributeFilter> = buildEntitySpecificNullFilters(
        split.where,
        split.extraAttributeFilter,
        remappedFields,
        columnRemappings,
        discriminatorLabels,
        discriminatorField
      );
      if (entityNullFilters.length === 0) {
        return of(response);
      }
      const retryFilter: Array<GridBackendAttributeFilter> = [
        ...columnAttributeFilter,
        ...split.extraAttributeFilter.filter(
          (entry: GridBackendAttributeFilter): boolean =>
            entry.operator !== 'IS_NULL' && entry.operator !== 'IS_NOT_NULL'
        ),
        ...entityNullFilters
      ];
      return queryElementsGrid(host, split.where, retryFilter, after);
    })
  );
}

/**
 * Loads null-bucket rows by paginating unfiltered `elements` queries and matching
 * bucket segments client-side. Used when backend `IS_NULL` attribute filters return empty.
 */
export function fetchNullBucketViaClientFilter(
  host: NullBucketHost,
  path: string,
  bucketWhere: Record<string, unknown>,
  scanAfter: string | undefined,
  pageSize: number,
  expectedTotal: number,
  alreadyLoaded: number
): Observable<GridRowsPage> {
  const unfilteredWhere: Record<string, unknown> = buildUnfilteredWhereForNullBucket(host, bucketWhere);
  const columnFilters: ColumnFilterMap = host.getColumnFilters();
  const sharedColumns: Array<DataGridColumn> = host.getSharedColumns();
  const discriminatorColumns: Record<string, Array<DataGridColumn>> = host.getTypeSpecificColumns();
  const remappedFields: ReadonlySet<string> = host.getRemappedFields();
  const columnAttributeFilter: Array<GridBackendAttributeFilter> = buildCurrentAttributeFilters(
    columnFilters,
    sharedColumns,
    discriminatorColumns,
    remappedFields
  );
  const attributeFilter: Array<GridBackendAttributeFilter> | undefined =
    columnAttributeFilter.length > 0 ? columnAttributeFilter : undefined;
  const targetNewRows: number = Math.min(pageSize, Math.max(0, expectedTotal - alreadyLoaded));

  return scanUnfilteredPagesForNullBucket(
    host,
    path,
    unfilteredWhere,
    attributeFilter,
    scanAfter,
    targetNewRows,
    expectedTotal,
    alreadyLoaded,
    0,
    new Array<GridRow>()
  );
}
